import * as React from 'react'

import { Info } from 'lucide-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  // Touch build: no hover, so the info opens on tap (Popover) instead of hover.
  isMobile: boolean
  // Navigate/exit to the Workflow Configurator. Present only where that's
  // possible (desktop); undefined → no link.
  onManage?: () => void
}

// Mobile popover restyled to match the dark tooltip bubble.
const MOBILE_BUBBLE_CLASS =
  'tw:w-auto tw:max-w-[260px] tw:gap-0 tw:rounded-md tw:bg-foreground tw:px-3 tw:py-1.5 tw:text-xs tw:text-background'

/**
 * The right-aligned Info affordance on an inactive (creation-disabled) workflow
 * type row. Explains the state and points to the Workflow Configurator — as a
 * working link on desktop, or a "use the Desktop version" hint on mobile. Only
 * admins reach this selector, so there's no member variant.
 */
function InactiveWorkflowTypeInfo({ isMobile, onManage }: IProps) {
  const { translator } = Localization

  const secondPart =
    isMobile || !onManage ? (
      <span>{translator.manageInWorkflowConfiguratorOnDesktop}</span>
    ) : (
      <>
        {translator.manageItIn}{' '}
        {/* Base UI tooltips are hoverable, so the link stays reachable. */}
        <button
          type="button"
          className="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-inherit tw:underline"
          onClick={e => {
            e.stopPropagation()
            onManage()
          }}
        >
          {translator.workflowConfigurator}
        </button>
      </>
    )

  // Single child so the bubble's inline-flex row doesn't split the sentence.
  const content = (
    <span>
      {translator.inactiveWorkflowTypeTooltip} {secondPart}
    </span>
  )

  // shadcn ghost Button: enlarged, easier-to-tap hit area around the icon, with
  // a keyboard-only focus ring (no ring on tap). base-ui composes it as the
  // tooltip/popover trigger via `render`.
  const trigger = (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={translator.inactiveWorkflowTypeTooltip}
      className="tw:text-muted-foreground"
    >
      <Info size={16} />
    </Button>
  )

  return (
    // Required, not defensive: inactive types ARE selectable, so the row's
    // onClick/onKeyDown would toggle the selection. This affordance swallows
    // its own click and Enter/Space so opening the tooltip never picks a type.
    <span
      className="tw:ml-auto tw:-my-1 tw:flex tw:shrink-0 tw:items-center"
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      {isMobile ? (
        // Popover gives tap-to-open + outside-tap dismiss for free.
        <Popover>
          <PopoverTrigger render={trigger} />
          <PopoverContent side="top" className={MOBILE_BUBBLE_CLASS}>
            {content}
          </PopoverContent>
        </Popover>
      ) : (
        <Tooltip>
          <TooltipTrigger render={trigger} />
          <TooltipContent>{content}</TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}

export default InactiveWorkflowTypeInfo
